import { supabase } from '@/shared/lib/supabase';
import { applyWindow, probeLimit, type ListWindow } from '@/shared/lib/listWindow';
import { EdgeFunctionError } from '@/domains/customers/services/adminCustomerService';

/**
 * adminNotificationService — Sprint Admin-B B-3.
 *
 * Read-side queries `v_admin_email_log` (admin-fenced view). Resend mutation
 * goes via the `notification-resend` Edge Function.
 *
 * Spec: _bmad-output/SPRINT_ADMIN_B_IMPLEMENTATION_PLAN_2026-05-13.md §4.
 */

export interface AdminEmailLogRow {
  id: string;
  created_at: string;
  sent_at: string | null;
  to_email: string;
  from_email: string;
  subject: string;
  template: string | null;
  provider: string | null;
  provider_message_id: string | null;
  status: string;
  error_message: string | null;
  related_entity_kind: string | null;
  related_entity_id: string | null;
  customer_id: string | null;
  parent_email_log_id: string | null;
  customer_email: string | null;
  related_href: string | null;
}

export interface ResendResult {
  ok: true;
  queued_outbox_id: string;
  eta_seconds: number;
  deduped: boolean;
}

interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface ListFilters {
  template?: string;
  status?: string;
  search?: string;       // free-text on to_email / subject
  date_from?: string;    // ISO date
  date_to?: string;      // ISO date
  limit?: number;
  offset?: number;
}

export const adminNotificationService = {
  /**
   * AR-6. Measured 2026-09-13: 538 rows total, 180 per 30 days, against a
   * default page of 50 — about 8.5 days visible. This page keeps its
   * server-side filters AND its offset pagination (ruling amendment A5:
   * converting it to client-side would have deleted working pagination and
   * capped it at the first 50 rows). The probe only makes the cap honest.
   */
  async list(filters: ListFilters = {}): Promise<ListWindow<AdminEmailLogRow>> {
    const limit = filters.limit ?? 50;
    let q = supabase
      .from('v_admin_email_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(probeLimit(limit));

    if (filters.offset !== undefined && filters.offset > 0) {
      // The range must span probeLimit, not limit — `range()` overrides
      // `.limit()`, so the narrower window would have silently swallowed the
      // AR-6 probe row and `truncated` would never have fired while paging.
      q = q.range(filters.offset, filters.offset + probeLimit(limit) - 1);
    }
    if (filters.template) q = q.eq('template', filters.template);
    if (filters.status) q = q.eq('status', filters.status);
    if (filters.date_from) q = q.gte('created_at', filters.date_from);
    if (filters.date_to) q = q.lte('created_at', filters.date_to);
    if (filters.search) {
      // OR across to_email + subject
      q = q.or(`to_email.ilike.%${filters.search}%,subject.ilike.%${filters.search}%`);
    }

    const { data, error } = await q;
    if (error) throw new Error(`Could not load email log: ${error.message}`);
    return applyWindow((data ?? []) as AdminEmailLogRow[], limit);
  },

  /** Lists distinct templates for the typeahead filter. */
  async listTemplates(): Promise<string[]> {
    const { data, error } = await supabase
      .from('v_admin_email_log')
      .select('template')
      .not('template', 'is', null)
      .limit(500);
    if (error) throw new Error(`Could not load templates: ${error.message}`);
    const set = new Set<string>();
    for (const row of (data ?? []) as Array<{ template: string | null }>) {
      if (row.template) set.add(row.template);
    }
    return [...set].sort();
  },

  async getOne(id: string): Promise<AdminEmailLogRow | null> {
    const { data, error } = await supabase
      .from('v_admin_email_log')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw new Error(`Could not load email: ${error.message}`);
    return (data as AdminEmailLogRow | null) ?? null;
  },

  /**
   * Look up the original notification_outbox payload for preview. v1 — JSON view.
   * v2 (G6.2 territory) will server-side render HTML; deferred per Q-B4 lock.
   */
  async getPayload(emailLogId: string): Promise<Record<string, unknown> | null> {
    const { data, error } = await supabase
      .from('notification_outbox')
      .select('payload')
      .eq('email_log_id', emailLogId)
      .maybeSingle();
    if (error) return null;
    return (data as { payload: Record<string, unknown> } | null)?.payload ?? null;
  },

  async resend(emailLogId: string): Promise<ResendResult> {
    const idempotencyKey = crypto.randomUUID();
    const { data, error } = await supabase.functions.invoke<ResendResult | ApiError>(
      'notification-resend',
      {
        body: { email_log_id: emailLogId },
        headers: { 'Idempotency-Key': idempotencyKey },
      },
    );
    if (error) {
      const ctx = (error as unknown as { context?: Response }).context;
      if (ctx && typeof ctx.clone === 'function') {
        try {
          const parsed = (await ctx.clone().json()) as ApiError;
          if (parsed.code && parsed.message) {
            throw new EdgeFunctionError(parsed.code, parsed.message, ctx.status, parsed.details);
          }
        } catch (e) {
          if (e instanceof EdgeFunctionError) throw e;
        }
      }
      throw new Error(error.message);
    }
    if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
      const e = data as ApiError;
      throw new EdgeFunctionError(e.code, e.message, 200, e.details);
    }
    return data as ResendResult;
  },
};
